import { useMemo, useCallback } from "react"
import { Slide, Template } from "types/ReportingDashboard/reporting.types"
import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"

/**
 * Parameters for the useMergedSlideContent hook
 */
interface MergedSlideContentParams {
  /** Currently active slide */
  activeSlide: Slide | undefined
  /** Object containing current templates */
  currentTemplates: Record<string, Template>
  /** Whether a template is currently being edited */
  isEditingTemplate: boolean
  /** ID of the template being edited */
  editingTemplate: string | null
}

/**
 * Merged slide content structure
 */
interface MergedSlideContent {
  /** Array of elements from template and/or slide */
  elements: SlideElement[]
  /** Merged table configurations */
  tableConfigs: Record<string, DataElementConfig>
}

/**
 * Return type for the useMergedSlideContent hook
 */
interface MergedSlideContentState {
  /** Memoized merged content for the currently active slide */
  mergedSlideContent: MergedSlideContent
  /** Function to get merged content for any slide */
  getMergedSlideForDisplay: (slide: Slide | undefined) => MergedSlideContent
}

/**
 * Custom hook for merging template and slide content into a unified display format.
 *
 * Handles three main scenarios:
 * 1. Template editing mode: Shows only template elements
 * 2. Slide with template: Merges template elements with slide-specific elements
 * 3. Slide without template: Shows only slide-specific elements
 *
 * Elements are marked with `isTemplateElement` flag to distinguish their source.
 * Table configurations are merged with slide configs taking precedence over template configs.
 *
 * @function useMergedSlideContent
 * @param params - Configuration object for content merging
 * @returns Object containing merged content and utility functions
 */
export const useMergedSlideContent = ({
  activeSlide,
  currentTemplates,
  isEditingTemplate,
  editingTemplate,
}: MergedSlideContentParams): MergedSlideContentState => {
  /**
   * Merges template and slide content for display purposes.
   * Handles different modes: template editing, slide with template, slide without template.
   *
   * @function getMergedSlideForDisplay
   * @param slide - The slide to get merged content for
   * @returns Merged content with elements and table configs
   */
  const getMergedSlideForDisplay = useCallback(
    (slide: Slide | undefined): MergedSlideContent => {
      // Return empty content if no slide provided
      if (!slide) return { elements: [], tableConfigs: {} }

      // Template editing mode: show only template elements
      if (isEditingTemplate && editingTemplate) {
        const templateContent: Template | undefined =
          currentTemplates[editingTemplate]
        return {
          elements: (templateContent?.elements || []).map(
            (el: SlideElement) => ({
              ...el,
              isTemplateElement: true,
            })
          ),
          tableConfigs: templateContent?.tableConfigs || {},
        }
      }

      // Slide without template: show only slide elements
      if (!slide.templateId || !currentTemplates[slide.templateId]) {
        return {
          elements: (slide.slideElements || []).map((el: SlideElement) => ({
            ...el,
            isTemplateElement: false,
          })),
          tableConfigs: slide.slideTableConfigs || {},
        }
      }

      // Slide with template: merge template and slide elements
      const template: Template = currentTemplates[slide.templateId]
      const templateElementsWithMarking: SlideElement[] = (
        template.elements || []
      ).map((el: SlideElement) => ({
        ...el,
        isTemplateElement: true,
      }))
      const slideElementsWithMarking: SlideElement[] = (
        slide.slideElements || []
      ).map((el: SlideElement) => ({
        ...el,
        isTemplateElement: false,
      }))

      return {
        elements: [...templateElementsWithMarking, ...slideElementsWithMarking],
        tableConfigs: {
          ...(template.tableConfigs || {}),
          ...(slide.slideTableConfigs || {}), // Slide configs override template configs
        },
      }
    },
    [currentTemplates, isEditingTemplate, editingTemplate]
  )

  /**
   * Memoized merged content for the currently active slide.
   * Recalculates only when dependencies change to optimize performance.
   */
  const mergedSlideContent: MergedSlideContent = useMemo(
    (): MergedSlideContent => getMergedSlideForDisplay(activeSlide),
    [getMergedSlideForDisplay, activeSlide]
  )

  return { mergedSlideContent, getMergedSlideForDisplay }
}
